export type PreloadItem = {
  type: "image" | "audio" | "video" | "other" | "import";
  src: string;
};

function loadItem(item: {
  type: "image" | "audio" | "video" | "other" | "import";
  src: string;
}): Promise<unknown> {
  switch (item.type) {
    case "image": {
      const img = new Image();
      img.src = item.src;
      return new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
    }

    case "audio": {
      const audio = new Audio();
      audio.src = item.src;
      return new Promise((resolve, reject) => {
        audio.oncanplaythrough = resolve;
        audio.onerror = reject;
        audio.onabort = reject;
        audio.load(); // iOS needs this!
      });
    }

    case "video": {
      const video = document.createElement("video");
      video.src = item.src;
      return new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
      });
    }

    case "import": {
      return import(/* @vite-ignore */ item.src);
    }

    case "other":
    default: {
      // Unsupported type. Just fetch it and cross fingers
      // that the browser will cache it.
      return fetch(item.src);
    }
  }
}

export function createAssetPreloader(
  items: PreloadItem[],
  onProgress?: (progress: number, item: PreloadItem) => void,
  onError?: (error: Error, item: PreloadItem) => void,
  concurrency: number = 5
): Promise<void> {
  return new Promise((resolve) => {
    let i = 0;
    const pendingItems = [...items];
    const loadingItems: PreloadItem[] = [];

    const maybeLoadNext = () => {
      if (loadingItems.length >= concurrency) {
        return;
      }

      const item = pendingItems.shift();
      if (!item) {
        return;
      }

      loadingItems.push(item);

      loadItem(item)
        .then(() => onProgress?.(i / items.length, item))
        .catch((error) => onError?.(error, item))
        .finally(() => {
          loadingItems.splice(loadingItems.indexOf(item), 1);
          i++;

          if (i === items.length) {
            resolve();
          } else {
            // Load the next item
            maybeLoadNext();
          }
        });

      // Fill the queue with initial items
      maybeLoadNext();
    };

    // Start the loading process
    maybeLoadNext();
  });
}
